import React, { useEffect } from 'react';
import Styled from 'styled-components';
import { reduxForm } from 'redux-form';
import { Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import { flatten, isEmpty, isNil, uniq } from 'ramda';

import ColumnHeader from '../../../../../components/ColumnHeader';
import Data from '../../../../../components/Table/Data';
import Spinner from '../../../../../components/Spinner';
import SetAsideProgramDetail from './SetAsideProgramDetail';

import messages from './messages';
import validate from './validate';
import { reduceFormValues } from './utils';

const StyledSetAsideSetupForm = Styled.form`
  table.hud-limits-form-table {
    td > div.form-group {
      &,
      &.has-error > .help-block,
      &.has-error {
        margin-bottom: 0;
      }

      & > .help-block:empty {
        margin: 0;
      }

      &.form-group.has-error {
        max-width: 140px;
      }

      .right-calendar .rdtPicker {
        right: 0;
      }

      .rdt {
        min-width: 120px;
      }
    }

    & > tbody > tr:not(:first-child) > td {
      border: none;
    }

    & > tbody > tr > td:first-child,
    & > tbody > tr > td:nth-child(2),
    & > thead > tr > tr th:first-child,
    & > thead > tr > tr th:nth-child(2) {
      min-width: 100px;
    }
  }
`;

const submit = (values, dispatch, props) => {
  const { onFormSubmit, setAsidePrograms } = props;
  const reducedFormValues = reduceFormValues(values, setAsidePrograms);
  onFormSubmit(reducedFormValues);
};

const onChange = (values, dispatch, props) => {
  const {
    formState: { setIsEmptyForm },
  } = props;
  const formValues = Object.values(values).map((program) =>
    Object.values(program),
  );
  const flat = flatten(formValues);
  setIsEmptyForm(
    uniq(flat).filter(
      (fieldValue) => !isNil(fieldValue) && !isEmpty(fieldValue),
    ).length === 0,
  );
};

const SetAsideSetupForm = ({
  intl,
  setAsidePrograms,
  isLoading,
  initialValues,
  handleSubmit,
  onFormSubmit,
  reset,
  submitting,
  valid,
  pristine,
  formState,
  dispatch,
}: Object) => {
  useEffect(() => {
    const { setIsValidForm, setIsFormSubmitting, setIsFormPristine } =
      formState;
    setIsValidForm(valid);
    setIsFormSubmitting(submitting);
    setIsFormPristine(pristine);
  }, [submitting, valid, pristine]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <StyledSetAsideSetupForm onSubmit={handleSubmit}>
      <Table className="hud-limits-form-table">
        <thead>
          <tr>
            <ColumnHeader
              columnLabel={[intl.formatMessage(messages.programName)]}
            />
            <ColumnHeader
              columnLabel={[intl.formatMessage(messages.setAside)]}
            />
            <ColumnHeader
              columnLabel={[intl.formatMessage(messages.numberOfUnits)]}
            />
            <ColumnHeader
              columnLabel={[intl.formatMessage(messages.incomeLimitPercentage)]}
            />
            <ColumnHeader
              columnLabel={[
                intl.formatMessage(messages.medianIncomePercentage),
              ]}
            />
            <ColumnHeader
              columnLabel={[intl.formatMessage(messages.maxRentPercentage)]}
            />
            <ColumnHeader
              columnLabel={[
                intl.formatMessage(messages.overIncomeRecertPercentage),
              ]}
            />
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <Data colSpan={8}>
                <Spinner />
              </Data>
            </tr>
          ) : (
            setAsidePrograms.map((setAsideProgram) => {
              return (
                <SetAsideProgramDetail
                  key={setAsideProgram.setAsideProgram.id}
                  setAsideProgram={setAsideProgram.setAsideProgram}
                  intl={intl}
                />
              );
            })
          )}
        </tbody>
      </Table>
    </StyledSetAsideSetupForm>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { initialValues } = ownProps;
  return {
    initialValues,
    onSubmit: submit,
    onChange: onChange,
  };
};

// $FlowFixMe
export default connect(mapStateToProps)(
  reduxForm({
    form: 'setAsideSetupForm',
    enableReinitialize: true,
    touchOnChange: true,
    validate,
  })(SetAsideSetupForm),
);
